import { useState, useCallback, useContext } from 'react';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { FirebaseContext } from '../context/FirebaseContext';

interface TokenCache {
  token: string;
  expiresAt: number;
}

export const useGoogleToken = () => {
  const firebase = useContext(FirebaseContext);
  const [tokenCache, setTokenCache] = useState<TokenCache | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getValidToken = useCallback(async (): Promise<string> => {
    const user = firebase.auth.currentUser;
    
    if (!user) {
      throw new Error("Usuario no autenticado");
    }

    // Verificar cache
    const now = Date.now();
    if (tokenCache && tokenCache.expiresAt > now) {
      return tokenCache.token;
    }

    setIsLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("https://www.googleapis.com/auth/spreadsheets");
      provider.addScope("https://www.googleapis.com/auth/drive.file");
      
      const result = await signInWithPopup(firebase.auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const accessToken = credential?.accessToken;
      
      if (!accessToken) {
        throw new Error("No se pudo obtener el token de acceso");
      }

      // Cache por 55 minutos
      const newCache = {
        token: accessToken,
        expiresAt: now + (55 * 60 * 1000)
      };
      setTokenCache(newCache);

      return accessToken;
    } finally {
      setIsLoading(false);
    }
  }, [firebase.auth, tokenCache]);

  const clearToken = useCallback(() => {
    setTokenCache(null);
  }, []);

  return {
    getValidToken,
    clearToken,
    isLoading,
    hasValidToken: tokenCache && tokenCache.expiresAt > Date.now()
  };
};
